import { useEffect, useState } from 'react';

import i18n from 'i18n-react';

import APIHandler from 'config/APIHandler';
import { storageKeys } from 'config/settings';
import StorageHandler from 'config/StorageHandler';

/** @param res */
const parseAdvisorResponse = (res) => {
  if (res === false || res == null) {
    return {
      data: null,
      error: i18n.translate('glassWidgets.errors.requestFailed'),
    };
  }
  if (typeof res !== 'object') {
    return { data: null, error: 'Unexpected response' };
  }
  const payload = res;

  if (payload.result === 'success') {
    return { data: res, error: null };
  }

  if (typeof payload.response === 'string') {
    return { data: null, error: payload.response };
  }
  if (typeof payload.message === 'string') {
    return { data: null, error: payload.message };
  }
  return { data: null, error: 'Request failed' };
};

/**
 * Builds the `lang` query value for getAdvisorDashboardData from storageKeys.lang
 *
 * @param lang
 */
const resolveDashboardLangCode = (lang) => {
  if (!lang || !lang.langFile) return 'en-US';
  const langFile = lang.langFile.toLowerCase();
  let region = String(lang.code || '').toUpperCase();
  if (!region || region === 'EN') {
    region = 'US';
  } else if (region === 'KO') {
    region = 'KR';
  }
  return `${langFile}-${region}`;
};

/**
 * Loads advisor dashboard payload via APIHandler.getAdvisorDashboardData (single request for glass widgets).
 *
 * @param appId
 * @param langCode Optional override; when omitted, read from storageKeys.lang in localStorage.
 */
const useAdvisorDashboardData = (appId, langCode) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (appId == null || appId < 0) {
      setData(null);
      setError(null);
      setIsLoading(false);
      return undefined;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    const fetchData = async () => {
      // TODO: Confirm implementation below meets requirements.
      /**
       * Checks for a user exists AND a token does not exists because the StorageHandler.exists() returns a falsy value
       * if the token is not both present AND valid. The redirect also checks for an existing user, because the homepage
       * is currently a public page and should not be locked behind a login page, but there is no refresh mechanic for
       * the accesstoken specifically. Should that ever be implemented, replace this section with one that checks the
       * access token validity and refreshes it if necessary.
       */
      const userExists = await StorageHandler.exists(storageKeys.user);
      const accessTokenExists = await StorageHandler.exists(
        storageKeys.accessToken,
      );
      if (userExists && !accessTokenExists) {
        window.location.href = '/logout';
        return;
      }

      const accessToken = await StorageHandler.load(storageKeys.accessToken);
      const accessTokenStorageKey =
        storageKeys.accessToken.name || storageKeys.accessToken;
      if (cancelled) return;
      if (!accessToken || StorageHandler.isExpired(accessTokenStorageKey)) {
        setData(null);
        setError(null);
        setIsLoading(false);
        return;
      }

      const storedLang = await StorageHandler.load(storageKeys.lang);
      const resolvedLangCode =
        langCode != null ? langCode : resolveDashboardLangCode(storedLang);
      const raw = await APIHandler.getAdvisorDashboardData(
        appId,
        accessToken,
        resolvedLangCode,
      );
      if (cancelled) return;
      const { data: next, error: err } = parseAdvisorResponse(raw);
      setData(next);
      setError(err);
      setIsLoading(false);
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [appId, langCode]);

  return { data, error, isLoading };
};

export default useAdvisorDashboardData;
